// Typed entity glyphs — one per entity kind, line-only stroke, single weight.
// Wrapper is .glyph from tokens.css (24px square, 1px border, optional tone).

import type { CSSProperties, ReactElement } from 'react';

export type GlyphType = 'OPP' | 'AGY' | 'VND' | 'VEH' | 'BID' | 'INT' | 'ALT' | 'CASE' | 'WL';
export type GlyphTone = 'pri' | 'grn' | 'amb' | 'rd' | 'mag' | 'cy';

interface GlyphProps {
  type: GlyphType;
  size?: 16 | 24 | 32;
  tone?: GlyphTone;
  className?: string;
  style?: CSSProperties;
}

const INNER: Record<GlyphType, ReactElement> = {
  OPP: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
    >
      <rect x="1.5" y="1.5" width="11" height="11" />
      <path d="M1.5 7H12.5M7 1.5V12.5" />
    </svg>
  ),
  AGY: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
    >
      <path d="M1.5 12.5V5L7 1.5L12.5 5V12.5" />
      <path d="M4 12.5V8M7 12.5V8M10 12.5V8" />
      <path d="M1.5 12.5H12.5" />
    </svg>
  ),
  VND: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
    >
      <path d="M7 1.5L12.5 4.5V9.5L7 12.5L1.5 9.5V4.5L7 1.5Z" />
    </svg>
  ),
  VEH: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
    >
      <path d="M1.5 1.5H12.5V12.5H1.5z" />
      <path d="M5 1.5V12.5M9 1.5V12.5M1.5 5H12.5M1.5 9H12.5" />
    </svg>
  ),
  BID: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
    >
      <path d="M3 1.5H9L11.5 4V12.5H3V1.5Z" />
      <path d="M9 1.5V4H11.5" />
      <path d="M5 7H9.5M5 9H9.5" />
    </svg>
  ),
  INT: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
    >
      <circle cx="7" cy="7" r="5.5" />
      <circle cx="7" cy="7" r="2.5" />
      <path d="M7 0V3M7 11V14M0 7H3M11 7H14" />
    </svg>
  ),
  ALT: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
    >
      <path d="M7 1.5L12.5 12.5H1.5L7 1.5Z" />
      <path d="M7 6V9M7 11V11.5" />
    </svg>
  ),
  CASE: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
    >
      <rect x="1.5" y="1.5" width="5" height="5" />
      <rect x="7.5" y="1.5" width="5" height="5" />
      <rect x="1.5" y="7.5" width="5" height="5" />
      <rect x="7.5" y="7.5" width="5" height="5" />
    </svg>
  ),
  WL: (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="1"
    >
      <path d="M2 2H12V12L7 9.5L2 12V2Z" />
    </svg>
  ),
};

export function Glyph({ type, size = 24, tone, className = '', style }: GlyphProps) {
  const cls = `glyph${size === 16 ? ' sm' : size === 32 ? ' lg' : ''}${tone ? ` ${tone}` : ''}${className ? ` ${className}` : ''}`;
  return (
    <span className={cls} style={{ width: size, height: size, ...style }}>
      {INNER[type]}
    </span>
  );
}
